import React from 'react';
import { Box, Typography, Paper, Button, Chip, Stack } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

const CHART_TYPE_LABELS = {
  table: 'Tabla',
  bar: 'Barras',
  line: 'Líneas',
  pie: 'Torta',
  kpi: 'KPI',
  intervals: 'Intervalos',
  heatmapGrid: 'Heatmap',
  imageDensity: 'Densidad',
  geoHeatmap: 'Mapa calor geo',
  map: 'Mapa',
};

function chartTypeLabel(item) {
  const t =
    item?.suggestedChartType ||
    item?.chartConfig?.type ||
    item?.wizardState?.visualization?.chartType ||
    '';
  return CHART_TYPE_LABELS[t] || t || '';
}

/**
 * Vista de solo lectura al seleccionar una referencia en la colección.
 * No carga el editor hasta que el usuario elija explícitamente editar el original.
 */
export default function ReferencePreviewPanel({
  original,
  entry,
  dashboardName = '',
  canMakeIndependent = false,
  makingIndependent = false,
  canRestoreToGrid = false,
  onEditOriginal,
  onMakeIndependent,
  onRestoreToGrid,
  onClose,
}) {
  const typeLabel = chartTypeLabel(original);
  const title = entry?.title || original?.title || 'Sin título';
  const collectionGroup = original?.collectionGroup || 'General';

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 280,
        p: 2,
        bgcolor: 'grey.100',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 420,
          width: '100%',
          p: 3,
          border: 2,
          borderStyle: 'dashed',
          borderColor: 'text.secondary',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'action.hover',
            opacity: 0.35,
            pointerEvents: 'none',
          }}
        />
        <Box sx={{ position: 'relative' }}>
          <LockOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="overline" color="text.secondary" display="block">
            No editable
          </Typography>
          <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Esta entrada es una <strong>referencia</strong> en el dashboard
            {dashboardName ? ` «${dashboardName}»` : ''}. No se puede modificar aquí para evitar
            cambios accidentales.
          </Typography>

          <Stack direction="row" spacing={0.75} justifyContent="center" flexWrap="wrap" sx={{ mb: 2.5 }}>
            <Chip
              size="small"
              icon={<LinkOutlinedIcon sx={{ fontSize: 14 }} />}
              label="Referencia"
              variant="outlined"
            />
            {typeLabel && <Chip size="small" label={typeLabel} variant="outlined" />}
            <Chip size="small" label={collectionGroup} variant="outlined" />
            {entry?.visibleOnGrid === false && (
              <Chip size="small" label="Oculto en grid" variant="outlined" color="warning" />
            )}
          </Stack>

          <Stack spacing={1}>
            <Button
              variant="contained"
              startIcon={<EditOutlinedIcon />}
              onClick={onEditOriginal}
              fullWidth
            >
              Ir a editar original
            </Button>
            {canMakeIndependent && onMakeIndependent && (
              <Button
                variant="outlined"
                startIcon={<CallSplitOutlinedIcon />}
                onClick={onMakeIndependent}
                disabled={makingIndependent}
                fullWidth
              >
                {makingIndependent ? 'Desacoplando…' : 'Desacoplar (original independiente)'}
              </Button>
            )}
            {canRestoreToGrid && onRestoreToGrid && (
              <Button
                variant="outlined"
                startIcon={<VisibilityOutlinedIcon />}
                onClick={onRestoreToGrid}
                disabled={makingIndependent}
                fullWidth
              >
                Mostrar en el grid
              </Button>
            )}
            <Button variant="text" onClick={onClose} fullWidth>
              Cerrar vista
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            {entry?.visibleOnGrid === false
              ? 'Este gráfico está oculto en el grid. Desacoplarlo crea una copia editable solo en este dashboard.'
              : 'Editar el original actualiza todas las referencias que lo usen.'}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
